import "@/components/Layout/Table/TableRow/TableRow.scss";

import clsx from "clsx";
import { type ChangeEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { Table } from "@/components/Layout/Table/Table";
import { TableHeader } from "@/components/Layout/Table/TableHeader/TableHeader";
import {
  addBasketEntry,
  addContract,
  addOption,
  checkSIM,
  listAvailableOptions,
  removeBasketEntry,
  saveBasket,
  setDeviceByUuid,
  setEntryCustomData,
  setICCID,
  useGetCustomer,
} from "@/services/api";
import { Basket } from "@/services/model";
import { getHeader } from "@/utils/basketUtils";
import { getDeviceForProduct } from "@/utils/productMatrix";

import {
  BASE_OPTION_PRODUCT_ID,
  type BulkActivationStatus,
  type BulkActivationStep,
  type BulkSimRowState,
  parseBulkSimCsv,
  ROW_DELAY_MS,
  setRowFailure,
  setRowStep,
  setRowSuccess,
  STEP_DELAY_MS,
  WAIT_POLL_INTERVAL_MS,
  WAIT_TIMEOUT_MS,
} from "./bulkSimActivationUtils";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const waitFor = async (
  predicate: () => boolean,
  timeoutMs = WAIT_TIMEOUT_MS,
  intervalMs = WAIT_POLL_INTERVAL_MS,
) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (predicate()) {
      return;
    }
    await sleep(intervalMs);
  }
  throw new Error("Timed out waiting for backend data");
};

const getFirstEntryUuid = (basket: Basket) => basket.entries["0"]?.uuid;

const getEntryIndexes = (basket: Basket) =>
  Object.keys(basket.entries)
    .map((index) => Number.parseInt(index, 10))
    .sort((a, b) => b - a);

const requireBasketId = (basket: Basket) => {
  if (!basket.id) {
    throw new Error("Basket id missing in backend response");
  }

  return basket.id;
};

const statusToneClassName: Record<BulkActivationStatus, string> = {
  pending: "text-foreground-secondary",
  running: "text-foreground-info",
  success: "text-foreground-success",
  failed: "text-foreground-error",
};

export const BulkSimActivation = () => {
  const { t } = useTranslation("portal", { keyPrefix: "bulk-sim-activation" });
  const { t: tDefault } = useTranslation();
  const { data: customer, isPending: isLoadingCustomer } = useGetCustomer();
  const [rows, setRows] = useState<BulkSimRowState[]>([]);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const stopRequestedRef = useRef(false);

  const displayName = [customer?.firstName, customer?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const customerLabel = displayName || customer?.email || t("customer.unknown");
  const paymentMode = customer?.paymentMethod?.paymentMode;
  const isManualPaymentMode = paymentMode === "Manual";

  const pendingRowsCount = useMemo(
    () => rows.filter((row) => row.status === "pending").length,
    [rows],
  );

  const getStepLabel = (step: BulkActivationStep) =>
    t(`steps.${step}` as const);

  const getStatusLabel = (status: BulkActivationStatus) =>
    t(`status.${status}` as const);

  const updateRowAt = (
    index: number,
    updater: (row: BulkSimRowState) => BulkSimRowState,
  ) => {
    setRows((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? updater(row) : row)),
    );
  };

  const onCsvSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const text = await file.text();
    const parsedRows = parseBulkSimCsv(text);
    if (parsedRows.length === 0) {
      setCsvError(t("csvError"));
      setRows([]);

      return;
    }

    setCsvError(null);
    setRows(parsedRows);
  };

  const processRows = async () => {
    if (!rows.length || isProcessing || !isManualPaymentMode) {
      return;
    }

    stopRequestedRef.current = false;
    setIsProcessing(true);

    for (let index = 0; index < rows.length; index += 1) {
      if (stopRequestedRef.current) {
        break;
      }

      const snapshot = rows[index];
      if (!snapshot || snapshot.status !== "pending") {
        continue;
      }

      try {
        updateRowAt(index, (row) => setRowStep(row, "checkSim"));
        const simCheck = await checkSIM(snapshot.iccid);
        if (simCheck.message !== "Sim is available" || !simCheck.optionId) {
          throw new Error("SIM is not available or no optionId returned");
        }
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "saveBasket"));
        let basket = await saveBasket();
        let basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        const existingIndexes = getEntryIndexes(basket);
        for (const basketIndex of existingIndexes) {
          basket = await removeBasketEntry(basketId, basketIndex);
          basketId = requireBasketId(basket);
          await sleep(STEP_DELAY_MS);
        }

        updateRowAt(index, (row) => setRowStep(row, "addBasketEntry"));
        basket = await addBasketEntry(basketId, BASE_OPTION_PRODUCT_ID, {
          isESIM: false,
        });
        basketId = requireBasketId(basket);
        const entryUuid = getFirstEntryUuid(basket);
        await waitFor(() => Boolean(entryUuid));
        if (!entryUuid) {
          throw new Error(
            "No basket entry uuid available after addBasketEntry",
          );
        }
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "setICCID"));
        basket = await setICCID(basketId, 0, {
          iccid: snapshot.iccid,
          puk: snapshot.puk,
          clientId: 1,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "setAlias"));
        basket = await setEntryCustomData(basketId, entryUuid, {
          SimAlias: snapshot.alias,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "addOption"));
        const options = await listAvailableOptions(basketId, 0);
        const matchingOption = options.find(
          (option) => option.product?.id === simCheck.optionId,
        );
        if (!matchingOption) {
          throw new Error("No matching option found for SIM optionId");
        }
        if (!matchingOption.precharged) {
          throw new Error("Only precharged SIM options are allowed");
        }
        basket = await addOption(basketId, entryUuid, {
          optionId: simCheck.optionId,
          quantity: 1,
          isONP: false,
        });
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "setDevice"));
        basket = await setDeviceByUuid(
          basketId,
          entryUuid,
          getDeviceForProduct(simCheck.optionId).id,
        );
        basketId = requireBasketId(basket);
        await waitFor(() => Boolean(basket.id));
        await sleep(STEP_DELAY_MS);

        updateRowAt(index, (row) => setRowStep(row, "checkout"));
        await addContract(
          {
            header: getHeader(),
            basketId,
          },
          { autoRenew: true },
        );

        updateRowAt(index, (row) => setRowSuccess(row));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t("activationFailed");
        updateRowAt(index, (row) => setRowFailure(row, row.step, message));
      }

      await sleep(ROW_DELAY_MS);
    }

    setIsProcessing(false);
    stopRequestedRef.current = false;
  };

  return (
    <section
      data-testid="bulk-sim-activation"
      className="flex w-full flex-col gap-gutter pb-8"
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-headline-xl font-semibold leading-xs text-foreground-primary">
          {t("heading")}
        </h1>
        <p className="text-body-sm leading-none text-foreground-primary">
          {t("description")}
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-md bg-surface-quaternary p-4">
        <h2 className="text-body-base font-semibold leading-md text-foreground-primary">
          {t("customer.heading")}
        </h2>
        {isLoadingCustomer ? (
          <p className="text-body-sm leading-none text-foreground-secondary">
            {t("customer.loading")}
          </p>
        ) : (
          <dl className="grid gap-2 text-body-sm leading-none text-foreground-secondary">
            <div className="flex flex-wrap gap-1">
              <dt className="font-semibold text-foreground-primary">
                {tDefault("Common.label.name")}:
              </dt>
              <dd>{customerLabel}</dd>
            </div>
            <div className="flex flex-wrap gap-1">
              <dt className="font-semibold text-foreground-primary">
                {t("customer.id")}:
              </dt>
              <dd>{customer?.id ?? t("customer.notAvailable")}</dd>
            </div>
            <div className="flex flex-wrap gap-1">
              <dt className="font-semibold text-foreground-primary">
                {t("customer.email")}:
              </dt>
              <dd>{customer?.email ?? t("customer.notAvailable")}</dd>
            </div>
            <div className="flex flex-wrap gap-1">
              <dt className="font-semibold text-foreground-primary">
                {t("customer.paymentMode")}:
              </dt>
              <dd>{paymentMode ?? t("customer.notAvailable")}</dd>
            </div>
          </dl>
        )}
      </div>

      {!isManualPaymentMode && !isLoadingCustomer && (
        <Notification
          dataTestid="bulk-sim-payment-warning"
          tone="warning"
          title={t("manualPaymentRequired.title")}
          description={t("manualPaymentRequired.description")}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-body-sm leading-none text-foreground-primary">
          <span className="sr-only">{t("csvFile")}</span>
          <input
            className="text-body-sm text-foreground-primary file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-surface-primary file:px-3 file:py-2 file:text-body-sm file:font-semibold file:text-foreground-primary-inverse disabled:cursor-not-allowed disabled:opacity-50"
            type="file"
            accept=".csv,text/csv"
            onChange={onCsvSelected}
            disabled={isProcessing}
          />
        </label>
        <Button
          variant="primary"
          disabled={!rows.length || isProcessing || !isManualPaymentMode}
          onClick={processRows}
        >
          {t("startActivation", { count: pendingRowsCount })}
        </Button>
        <Button
          variant="secondary"
          disabled={!isProcessing}
          onClick={() => {
            stopRequestedRef.current = true;
          }}
        >
          {t("stopAfterCurrent")}
        </Button>
      </div>

      {csvError && (
        <Notification
          dataTestid="bulk-sim-csv-error"
          tone="error"
          description={csvError}
        />
      )}

      <div className="flex flex-col rounded-lg bg-surface-accent-ivory p-4">
        <Table
          variant="plain"
          hasPaddingBottom={false}
          noRows={rows.length === 0}
          noRowsMessage={t("table.empty")}
        >
          <thead>
            <TableHeader>
              <th className="pl-6">
                {tDefault("portal:common.device.attributes.status")}
              </th>
              <th>{tDefault("portal:common.device.attributes.iccid")}</th>
              <th>{tDefault("portal:common.device.attributes.puk")}</th>
              <th>{t("table.alias")}</th>
            </TableHeader>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.iccid}-${index}`}
                className="row-layout text-body-base"
              >
                <td className="relative w-1 pl-6">
                  <div
                    className={clsx(
                      "font-semibold",
                      statusToneClassName[row.status],
                    )}
                  >
                    {getStatusLabel(row.status)}
                  </div>
                  <div className="text-body-xs leading-none text-foreground-secondary">
                    {getStepLabel(row.step)}
                  </div>
                  {row.errorMessage && (
                    <div className="text-body-xs leading-none text-foreground-error">
                      {row.errorMessage}
                    </div>
                  )}
                </td>
                <td>{row.iccid}</td>
                <td>{row.puk}</td>
                <td>{row.alias}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </section>
  );
};
